"use client"

import React, { useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { Slot } from "@radix-ui/react-slot"

import { GetSlug } from "@/lib/utils"
import {
  CardEvent,
  ChipFilterList,
  ICardEventProps,
  Pagination,
  TPagination,
} from "@/components/ui/molecules"

export interface EventsProps {
  asChild?: boolean
  componentTitle?: string
  className?: string
  cardEvents: Array<ICardEventProps>
  pageUrl?: string
  preview?: boolean
  codename: string
  totalPageCount?: number
}

const Events = React.forwardRef<HTMLDivElement, EventsProps>(
  (
    {
      asChild = false,
      className,
      cardEvents,
      componentTitle,
      pageUrl,
      totalPageCount,
      codename,
      preview = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"
    const [selectedTagLabel, setSelectedTagLabelState] = useState<
      string | null
    >(null)

    const filteringEl = "canton"

    const searchParams = useSearchParams()
    const router = useRouter()

    // Read params from URL
    const currentPageParam = searchParams.get("page")
    const currentPage = currentPageParam ? parseInt(currentPageParam, 10) : 1
    const filterParam = searchParams.get("subtheme")

    const limitEvents = 10

    // preview and fallback for pageUrl
    let baseUrl = pageUrl ?? "/"
    if (preview) {
      baseUrl = codename ?? baseUrl
    }

    // Initialize selectedTagLabel from URL param 'subtheme' if present
    const [initialized, setInitialized] = useState(false)
    if (!initialized) {
      setSelectedTagLabelState(filterParam ?? null)
      setInitialized(true)
    }

    // Wrapper to set selectedTagLabel AND update the URL so the filter persists
    const setSelectedTagLabel = (label: string | null) => {
      setSelectedTagLabelState(label)

      // Build new query params: reset page to 1 when changing filter
      const params = new URLSearchParams()
      if (label) {
        params.set("subtheme", label)
      }
      params.set("page", "1")

      const newUrl = `${baseUrl}?${params.toString()}`
      router.push(newUrl)
    }

    // Apply filter first (this affects total count and pagination)
    const filteredEvents = (cardEvents ?? []).filter((card) => {
      if (!selectedTagLabel) return true
      return `tag-${GetSlug(card[filteringEl])}` === selectedTagLabel
    })

    // Pagination calculations based on filtered list
    const totalEventsCount = filteredEvents.length
    const pageCount = Math.max(1, Math.ceil(totalEventsCount / limitEvents))
    const currentPageClamped = Math.min(
      Math.max(currentPage || 1, 1),
      pageCount
    )
    const offset = (currentPageClamped - 1) * limitEvents
    const eventsToShow = filteredEvents.slice(offset, offset + limitEvents)

    const showPagination = totalEventsCount > limitEvents

    // Build pagination list and include active subtheme param if present
    const paginationList: TPagination[] = []
    for (let i = 1; i <= pageCount; i++) {
      const params = new URLSearchParams()
      params.set("page", String(i))
      if (selectedTagLabel) {
        params.set("subtheme", selectedTagLabel)
      }
      const url = `${baseUrl}?${params.toString()}`

      paginationList.push({
        label: i,
        isCurrent: i === currentPageClamped,
        url,
      })
    }

    return (
      <Component
        className="mb-3 mt-10 bg-neutral-200 lg:mt-14"
        ref={ref}
        {...props}
      >
        <div className="container">
          <ChipFilterList
            componentTitle={componentTitle}
            list={cardEvents}
            filteringEl={filteringEl}
            className="mb-9 lg:mb-12"
            selectedTagLabel={selectedTagLabel}
            setSelectedTagLabel={setSelectedTagLabel}
          />
          {eventsToShow.map((cardEvent) => (
            <CardEvent
              className="mb-9 lg:mb-8"
              key={cardEvent.id}
              {...cardEvent}
            />
          ))}
        </div>
        {showPagination ? (
          <footer className="flex items-center justify-center pt-12">
            <Pagination list={paginationList} />
          </footer>
        ) : null}
      </Component>
    )
  }
)
Events.displayName = "Events"

export { Events }
